import { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { Card, PageTitle } from "./ui";

const Field = ({ label, ...props }) => (
  <div>
    <label className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase" htmlFor={props.id}>{label}</label>
    <input
      {...props}
      className="mt-2 w-full rounded-xl border border-white/10 bg-[#0B0B0F] px-4 py-3 text-sm text-white placeholder:text-[#3A3F4A] outline-none transition-colors focus:border-[#D4AF37]/60 disabled:opacity-50"
    />
  </div>
);

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    dob: user?.dob || "",
    state: user?.state || "",
    country: user?.country || "India",
    redeem_upi_id: user?.redeem_upi_id || "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "saving", message: "" });
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.put("/profile", payload);
      await refresh();
      setStatus({ state: "done", message: "Profile updated successfully." });
    } catch (err) {
      setStatus({ state: "error", message: err.response?.data?.detail || "Could not save your profile. Check the fields and try again." });
    }
  };

  return (
    <div data-testid="profile-page">
      <PageTitle title="Profile" sub="Your account details and payout information." />

      <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <Card data-testid="profile-identity-card">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 font-display text-xl font-bold text-[#D4AF37]">
              {(user?.name || user?.email || "W")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate font-display text-lg font-semibold text-white">{user?.name || "Winter Member"}</p>
              <p className="truncate text-sm text-[#99A1AF]" data-testid="profile-email">{user?.email}</p>
            </div>
          </div>
          <div className="mt-6 space-y-4 border-t border-white/[0.08] pt-6">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">User ID</p>
              <p className="mt-1 break-all font-mono text-xs text-[#99A1AF]" data-testid="profile-user-id">{user?.id}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">Wallet ID</p>
              <p className="mt-1 font-mono text-sm text-gold-gradient" data-testid="profile-wallet-id">{user?.wallet_id}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">Referral Code</p>
              <p className="mt-1 font-mono text-sm text-white">{user?.referral_code}</p>
            </div>
          </div>
        </Card>

        <Card data-testid="profile-edit-card">
          <h2 className="font-display text-lg font-semibold text-white">Edit Profile</h2>
          <form onSubmit={save} className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Full Name" id="pf-name" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="profile-name-input" />
            <Field label="Phone" id="pf-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" data-testid="profile-phone-input" />
            <Field label="Date of Birth" id="pf-dob" type="date" value={form.dob} onChange={set("dob")} data-testid="profile-dob-input" />
            <Field label="State" id="pf-state" value={form.state} onChange={set("state")} placeholder="Your state" data-testid="profile-state-input" />
            <Field label="Country" id="pf-country" value={form.country} onChange={set("country")} data-testid="profile-country-input" />
            <Field label="Payout UPI ID" id="pf-upi" value={form.redeem_upi_id} onChange={set("redeem_upi_id")} placeholder="name@upi" data-testid="profile-upi-input" />
            <div className="sm:col-span-2">
              {status.message && (
                <p className={`mb-3 text-sm ${status.state === "error" ? "text-red-400" : "text-emerald-400"}`} data-testid="profile-save-status">
                  {status.message}
                </p>
              )}
              <button
                type="submit"
                disabled={status.state === "saving"}
                data-testid="profile-save-button"
                className="rounded-full bg-[#D4AF37] px-7 py-3 text-sm font-semibold text-[#08080A] transition-colors hover:bg-[#F3E5AB] disabled:opacity-60"
              >
                {status.state === "saving" ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
